import {Alert, Badge, Button, Form, Modal, Spinner} from "react-bootstrap";
import React, {useEffect, useState} from "react";
import {FaKey, FaPlus, FaTrash} from "react-icons/fa";
import {getJobDetails, updateJobAdmin} from "../../service/JobService";
import {
    isSystemManagedChecklistItem,
    preserveSystemManagedChecklistItems,
} from "./checklistItemRules";

export function ChecklistStatusModal({show, job, handleClose, onJobUpdated}) {
    const [jobDetails, setJobDetails] = useState(null);
    const [checklists, setChecklists] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [message, setMessage] = useState("");
    const [messageType, setMessageType] = useState("");

    useEffect(() => {
        if (!show) return;
        setIsLoading(true);
        setMessage("");
        setMessageType("");
        getJobDetails(job.cos_job_id).then(response => {
            if (response.status === 200) {
                response.json().then(result => {
                    setJobDetails(result);
                    setChecklists(result.checklists || []);
                    setIsLoading(false);
                });
            } else {
                setIsLoading(false);
            }
        }).catch(error => {
            console.log('error', error);
            setIsLoading(false);
        });
    }, [show, job.cos_job_id]);

    function buildUpdatedJobPayload(updatedDetails) {
        const updatedChecklists = updatedDetails.checklists || [];
        const checklistCompleted = updatedChecklists.filter(item => item.completed).length;
        return {
            ...updatedDetails,
            checklist_total: updatedChecklists.length,
            checklist_completed: checklistCompleted,
            has_job_level_checklist: updatedChecklists.some(item => item.level === "JOB")
        };
    }

    function handleToggle(index) {
        if (isSystemManagedChecklistItem(checklists[index])) return;
        setChecklists(prev => prev.map((item, itemIndex) =>
            itemIndex === index ? {...item, completed: !item.completed} : item
        ));
    }

    function handleDescriptionChange(index, value) {
        if (isSystemManagedChecklistItem(checklists[index])) return;
        setChecklists(prev => prev.map((item, itemIndex) =>
            itemIndex === index ? {...item, description: value} : item
        ));
    }

    function handleLevelChange(index, value) {
        if (isSystemManagedChecklistItem(checklists[index])) return;
        setChecklists(prev => prev.map((item, itemIndex) =>
            itemIndex === index ? {...item, level: value} : item
        ));
    }

    function handleAdd() {
        setChecklists(prev => [...prev, {description: "", completed: false, level: "JOB"}]);
    }

    function handleRemove(index) {
        if (isSystemManagedChecklistItem(checklists[index])) return;
        setChecklists(prev => prev.filter((_, itemIndex) => itemIndex !== index));
    }

    function handleSave() {
        if (!jobDetails) return;

        setIsSaving(true);
        setMessage("");
        setMessageType("");

        updateJobAdmin({
            ...jobDetails,
            checklists: preserveSystemManagedChecklistItems(jobDetails.checklists, checklists),
        }).then(response => {
            if (response.status === 200) {
                response.json().then(result => {
                    setJobDetails(result);
                    setChecklists(result.checklists || []);
                    setMessage("Checklist saved successfully");
                    setMessageType("success");
                    if (onJobUpdated) {
                        onJobUpdated(buildUpdatedJobPayload(result));
                    }
                });
            } else {
                response.text().then(result => {
                    setMessage(result || "Failed to save checklist");
                    setMessageType("danger");
                });
            }
        }).catch(error => {
            console.log('error', error);
            setMessage("An error occurred while saving.");
            setMessageType("danger");
        }).finally(() => setIsSaving(false));
    }

    const completedCount = checklists.filter(item => item.completed).length;

    return (
        <Modal show={show} onHide={handleClose} onClick={(e) => e.stopPropagation()} size="lg">
            <Modal.Header closeButton>
                <Modal.Title>Checklist - {job.cos_job_id}</Modal.Title>
            </Modal.Header>
            <Modal.Body style={{textAlign: "left"}}>
                <div style={{marginBottom: "8px"}}>
                    <strong>Address:</strong> {job.full_address}
                </div>
                {isLoading ? (
                    <div style={{textAlign: "center", padding: "20px"}}>
                        <Spinner animation="border" size="sm"/> Loading...
                    </div>
                ) : (
                    <>
                        <div style={{display: "flex", alignItems: "center", marginBottom: "12px"}}>
                            {checklists.length > 0 && (
                                <Badge bg={completedCount === checklists.length ? "success" : "warning"}>
                                    {completedCount}/{checklists.length} completed
                                </Badge>
                            )}
                            <Button variant="outline-primary" size="sm" onClick={handleAdd} style={{marginLeft: "12px"}}>
                                <FaPlus style={{marginRight: "4px"}}/> Add Item
                            </Button>
                        </div>

                        {checklists.length === 0 && (
                            <Alert variant="info" className="mb-3">
                                No checklist items. Click "Add Item" to create one.
                            </Alert>
                        )}

                        {checklists.map((item, index) => {
                            const systemManaged = isSystemManagedChecklistItem(item);
                            return (
                            <div key={item.id || `new-${index}`}
                                 style={{
                                     display: "flex",
                                     alignItems: "center",
                                     padding: "8px 12px",
                                     marginBottom: "4px",
                                     borderRadius: "4px",
                                     backgroundColor: item.completed ? "#f0fdf0" : "#fff",
                                     border: "1px solid #dee2e6"
                                 }}>
                                <Form.Check
                                    type="checkbox"
                                    checked={Boolean(item.completed)}
                                    disabled={systemManaged}
                                    aria-label={systemManaged ? "System-managed locker password status" : undefined}
                                    onChange={() => handleToggle(index)}
                                    style={{marginRight: "12px"}}
                                />
                                <Form.Control
                                    type="text"
                                    size="sm"
                                    value={item.description || ""}
                                    readOnly={systemManaged}
                                    placeholder="Checklist item description..."
                                    onChange={(e) => handleDescriptionChange(index, e.target.value)}
                                    style={{
                                        flex: 1,
                                        textDecoration: item.completed ? "line-through" : "none",
                                        color: item.completed ? "#6c757d" : "#212529"
                                    }}
                                />
                                <Form.Select
                                    size="sm"
                                    value={item.level || "JOB"}
                                    disabled={systemManaged}
                                    onChange={(e) => handleLevelChange(index, e.target.value)}
                                    style={{width: "120px", marginLeft: "8px"}}
                                >
                                    <option value="JOB">JOB</option>
                                    <option value="PROPERTY">PROPERTY</option>
                                </Form.Select>
                                {systemManaged ? (
                                    <Badge bg={item.completed ? "success" : "warning"} style={{marginLeft: "8px"}}>
                                        <FaKey className="me-1"/>
                                        {item.completed ? "Password updated" : "Worker entry required"}
                                    </Badge>
                                ) : (
                                    <Button
                                        variant="outline-danger"
                                        size="sm"
                                        aria-label="Remove checklist item"
                                        onClick={() => handleRemove(index)}
                                        style={{marginLeft: "8px"}}
                                    >
                                        <FaTrash/>
                                    </Button>
                                )}
                            </div>
                        )})}

                        {message && (
                            <Alert variant={messageType} className="mb-0 mt-3">
                                {message}
                            </Alert>
                        )}
                    </>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="primary" onClick={handleSave} disabled={isLoading || isSaving}>
                    {isSaving ? <>
                        <Spinner as="span" animation="border" size="sm" className="me-2"/>
                        Saving...
                    </> : "Save Checklist"}
                </Button>
                <Button variant="secondary" onClick={handleClose}>Close</Button>
            </Modal.Footer>
        </Modal>
    );
}
